/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Calendar,
  Users,
  Award,
  Code,
  Brain,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Menu,
  X,
  LogIn,
  Lock,
  FileText,
  Upload,
  Camera,
} from "lucide-react";
import { databases, account, storage } from "./appwrite"; // Added storage import
import { ID, Query } from "appwrite";
import AdminDashboard from "./AdminDashboard";
import AttendanceDashboard from "./AttendanceDashboard";

// Configuration Constants
const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
const COL_ID =
  process.env.REACT_APP_APPWRITE_COLLECTION_ID || "692a9f82002dabe4ac9c";
const WORKSHOP_CAPACITY = 40;

export default function MMTTCWorkshop() {
  const [view, setView] = useState("landing"); // 'landing' | 'admin' | 'dashboard'
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [seatsRemaining, setSeatsRemaining] = useState(WORKSHOP_CAPACITY);

  // --- Global Effects ---
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    fetchSeats();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const fetchSeats = async () => {
    try {
      const res = await databases.listDocuments(DB_ID, COL_ID, [
        Query.limit(1),
      ]);
      setSeatsRemaining(Math.max(0, WORKSHOP_CAPACITY - res.total));
    } catch (err) {
      console.error("Error fetching seats:", err);
    }
  };

  const scrollToSection = (id) => {
    if (view !== "landing") {
      setView("landing");
      setTimeout(
        () =>
          document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }),
        100
      );
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 font-sans text-gray-900">
      {/* --- Navigation Header --- */}
      <header
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? "bg-white/95 backdrop-blur-lg shadow-lg"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div
              className="flex items-center space-x-4 cursor-pointer"
              onClick={() => setView("landing")}
            >
              <div className="w-14 h-14 bg-gradient-to-br from-brown-600 to-brown-700 rounded-xl flex items-center justify-center shadow-lg transform hover:scale-105 transition-transform">
                <img
                  src="/logo1.png"
                  alt="Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>

              <div className="leading-tight">
                <h1 className="font-semibold text-lg text-gray-900 tracking-wide">
                  Malviya Mission Teacher Training Centre
                </h1>
                <p className="text-sm text-gray-600 font-medium">
                  In collaboration with SIIEDC, University of Jammu
                </p>
              </div>
            </div>

            <nav className="hidden md:flex items-center space-x-8">
              {view === "landing" ? (
                <>
                  <button
                    onClick={() => scrollToSection("about")}
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    About
                  </button>
                  <button
                    onClick={() => scrollToSection("focus")}
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Program
                  </button>
                  <button
                    onClick={() => scrollToSection("team")}
                    className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Team
                  </button>
                  <button
                    onClick={() => scrollToSection("register")}
                    className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-2.5 rounded-lg font-semibold hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
                  >
                    Register Now
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setView("landing")}
                  className="text-gray-700 hover:text-blue-600 transition-colors font-medium"
                >
                  Back to Home
                </button>
              )}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-gray-700"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <button
                onClick={() => scrollToSection("about")}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection("focus")}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-blue-50 rounded-lg"
              >
                Program
              </button>
              <button
                onClick={() => scrollToSection("register")}
                className="block w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-lg font-semibold"
              >
                Register Now
              </button>
            </div>
          </div>
        )}
      </header>

      {/* --- Main Content Switcher --- */}
      <main className="pt-20">
        {view === "landing" && (
          <>
            <LandingPage
              seatsRemaining={seatsRemaining}
              fetchSeats={fetchSeats}
            />
            <OrganizingCommittee />
          </>
        )}
        {view === "admin" && (
          <AdminLogin onLoginSuccess={() => setView("dashboard")} />
        )}
        {view === "dashboard" && (
          <AdminDashboard
            onLogout={() => setView("landing")}
            onViewAttendance={() => setView("attendance_logs")}
          />
        )}
        {/* 🔥 ATTENDANCE DASHBOARD */}
        {view === "attendance_logs" && (
          <AttendanceDashboard
            onBack={() => setView("dashboard")} // Handle back button
          />
        )}
      </main>

      {/* --- Footer --- */}
      <footer className="bg-blue-900 text-gray-300 font-sans">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Top content */}
          <div className="grid md:grid-cols-3 gap-6">
            {/* Contact */}
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-blue-500 mb-2 font-serif">
                Contact
              </h4>
              <div className="space-y-1 text-sm font-light font-serif">
                <a
                  href="mailto:jatindermanhas@jammuuniversity.ac.in"
                  className="block hover:text-red-600"
                >
                  jatindermanhas@jammuuniversity.ac.in
                </a>
                <a
                  href="mailto:mohsin.hassan@jammuuniversity.ac.in"
                  className="block hover:text-red-600"
                >
                  mohsin.hassan@jammuuniversity.ac.in
                </a>
                <a
                  href="tel:+919419173793"
                  className="block hover:text-red-600"
                >
                  +91 94191 73793
                </a>
                <a
                  href="tel:+918492013757"
                  className="block hover:text-red-600"
                >
                  +91 84920 13757
                </a>
              </div>
            </div>

            {/* Organizer */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-500 mb-2 font-serif">
                Organized By
              </h4>
              <p className="text-sm font-light leading-snug font-serif">
                Malaviya Mission Teacher Training Centre
              </p>
              <p className="text-sm font-light leading-snug font-serif">
                In collaboration with SIIEDC, University of Jammu
              </p>
            </div>

            {/* Admin */}
            <div>
              <h4 className="text-xs font-semibold uppercase tracking-wide text-blue-500 mb-2 font-serif">
                Faculty Access
              </h4>
              <p className="text-xs font-light text-gray-400 mb-1 font-serif">
                Authorized faculty only.
              </p>
              <button
                onClick={() =>
                  setView(view === "landing" ? "admin" : "landing")
                }
                className="flex items-center gap-1 text-sm font-light text-blue-400 hover:text-blue-300"
              >
                <Lock className="w-4 h-4" />
                {view === "landing" ? "Faculty Login" : "Back to Home"}
              </button>
            </div>
          </div>

          {/* Bottom slim strip */}
          <div className="font-serif mt-3 border-t border-slate-800 pt-5 text-center text-xs  text-gray-400 space-y-2">
            <p>© 2026 University of Jammu. All Rights Reserved.</p>
            <p>
              Designed and Managed by{" "}
              <span className="text-red-600">
                <a href="https://www.linkedin.com/in/imohsinhassan">
                  Mohsin Hassan
                </a>
              </span>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ==========================================
// COMPONENT: Landing Page (With Expanded About & Upload Logic)
// ==========================================
function LandingPage({ seatsRemaining, fetchSeats }) {
  // Constants
  const BUCKET_ID = "mmttcregid";
  const DB_ID = process.env.REACT_APP_APPWRITE_DB_ID || "regdb";
  const COL_ID =
    process.env.REACT_APP_APPWRITE_COLLECTION_ID || "692a9f82002dabe4ac9c";

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    dob: "",
    whatsapp: "",
    designation: "",
    affiliation: "",
    department: "",
    contactMethod: "",
  });

  // Upload States
  const [nocFile, setNocFile] = useState(null);
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isPreviewed, setIsPreviewed] = useState(false);
  const [loading, setLoading] = useState(false);

  // Photo Upload States
  const [photoFile, setPhotoFile] = useState(null);
  const [photoFileId, setPhotoFileId] = useState(null);
  const [isPhotoUploading, setIsPhotoUploading] = useState(false);

  // Modal State
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  // --- Photo Upload Handlers ---
  const handlePhotoChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setPhotoFile(e.target.files[0]);
      setPhotoFileId(null);
    }
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    setIsPhotoUploading(true);
    try {
      const fileUpload = await storage.createFile(
        BUCKET_ID,
        ID.unique(),
        photoFile
      );
      setPhotoFileId(fileUpload.$id);
      alert("Photo uploaded successfully!");
    } catch (error) {
      console.error(error);
      alert("Photo upload failed. Please try again.");
    } finally {
      setIsPhotoUploading(false);
    }
  };

  // --- Registration Logic (Untouched) ---
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setNocFile(e.target.files[0]);
      setUploadedFileId(null);
      setIsPreviewed(false);
      setShowPreviewModal(false);
    }
  };

  const handleUploadNOC = async () => {
    if (!nocFile) return;
    setIsUploading(true);
    try {
      const fileUpload = await storage.createFile(
        BUCKET_ID,
        ID.unique(),
        nocFile
      );
      setUploadedFileId(fileUpload.$id);
      alert("File uploaded successfully! Please preview it to proceed.");
    } catch (error) {
      console.error(error);
      alert("Upload failed. Please try again.");
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreviewNOC = () => {
    if (!uploadedFileId) return;
    const url = storage.getFileView(BUCKET_ID, uploadedFileId);
    setPreviewUrl(url);
    setShowPreviewModal(true);
    setIsPreviewed(true);
  };

  const handleResetFile = () => {
    if (
      window.confirm(
        "Are you sure you want to change the file? You will need to upload and preview the new file again."
      )
    ) {
      setUploadedFileId(null);
      setIsPreviewed(false);
      setNocFile(null);
      setShowPreviewModal(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (seatsRemaining <= 0) return alert("No seats remaining.");

    //if (!uploadedFileId || !isPreviewed) {
    //  return alert("Please upload and preview the NOC document first.");
    //}

    setLoading(true);
    try {
      const existing = await databases.listDocuments(DB_ID, COL_ID, [
        Query.equal("email", formData.email),
      ]);
      if (existing.total > 0) {
        alert("This email is already registered.");
        setLoading(false);
        return;
      }

      await databases.createDocument(DB_ID, COL_ID, ID.unique(), {
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        dob: formData.dob,
        whatsapp: formData.whatsapp,
        designation: formData.designation,
        affiliation: formData.affiliation,
        department: formData.department,
        contactMethod: formData.contactMethod,
        photoFileId: photoFileId || null, // --- ADDED: Save Photo ID ---
        nocFileId: uploadedFileId || null,
        createdAt: new Date().toISOString(),
      });

      alert("Registration submitted successfully!");

      setFormData({
        fullName: "",
        email: "",
        phone: "",
        dob: "",
        whatsapp: "",
        designation: "",
        affiliation: "",
        department: "",
        contactMethod: "",
        photoFileId: "",
      });
      setNocFile(null);
      setUploadedFileId(null);
      setIsPreviewed(false);
      setShowPreviewModal(false);
      fetchSeats();
    } catch (err) {
      alert("Registration failed: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-12 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 opacity-5"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
            🎓 Faculty Development Program • RUSA-II Initiative
          </div>
          <h1 className="text-5xl md:text-7xl font-black text-gray-900 mb-6 leading-tight">
            Python for{" "}
            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              AI-Driven
            </span>
            <br />
            Teaching & Research
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mb-12">
            <div className="bg-white rounded-2xl shadow-xl p-6 min-w-[160px]">
              <Calendar className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <p className="text-lg font-bold text-gray-900">Jan 19-23, 2026</p>
            </div>
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl shadow-xl p-6 min-w-[160px]">
              <Users className="w-8 h-8 text-white mx-auto mb-2" />
              <p className="text-lg font-bold text-white">25 Seats</p>
            </div>
          </div>
        </div>
      </section>

      {/* --- ADDED: Program Focus & About (Expanded Form) --- */}
      <section id="focus" className="py-20 bg-white/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Focus Cards */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Program Focus
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Cutting-edge skills for modern academic professionals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mb-6">
                <Code className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Hands-on Python
              </h3>
              <p className="text-gray-600">
                Master Python fundamentals tailored for educators, focusing on
                automation and data handling.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-indigo-100 rounded-2xl flex items-center justify-center mb-6">
                <Brain className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                AI-Driven Teaching
              </h3>
              <p className="text-gray-600">
                Integrate Generative AI tools (ChatGPT, Gemini) into your
                teaching methodology to enhance student engagement.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow">
              <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-6">
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Research Workflows
              </h3>
              <p className="text-gray-600">
                Streamline research with Python automation for data analysis,
                visualization, and administrative efficiency.
              </p>
            </div>
          </div>

          {/* Expanded About Section */}
          <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
            <div className="md:w-1/3 bg-gradient-to-br from-blue-600 to-indigo-700 p-12 text-white flex items-center justify-center text-center">
              <div>
                <Award className="w-20 h-20 mx-auto mb-6" />
                <h3 className="text-3xl font-bold mb-4">
                  Excellence in
                  <br />
                  Academic Training
                </h3>
                <p className="text-blue-100">
                  RUSA-II Faculty Improvement Support
                </p>
              </div>
            </div>
            <div className="md:w-2/3 p-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                About the Workshop
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6 text-lg text-justify">
                This comprehensive five-day faculty training program is designed
                to equip educators with practical Python programming skills and
                AI integration strategies. Unlike theoretical courses, this
                workshop focuses on <strong>applied skills</strong> that can be
                immediately implemented in your daily academic workflow.
              </p>

              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Key Learning Outcomes
              </h3>
              <ul className="space-y-3">
                {[
                  "Automate routine academic & administrative tasks using Python",
                  "Effective Prompt Engineering for Research & Grant Writing",
                  "Data Analysis & Visualization for Research Papers",
                  "Building simple AI agents for student support",
                ].map((item, i) => (
                  <li key={i} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-8 p-4 bg-blue-50 border-l-4 border-blue-500 rounded-r-lg">
                <p className="text-sm text-blue-800 text-justify">
                  <strong>Target Audience:</strong> <br />
                  The program is designed for Faculty members from any
                  discipline (Sciences, Humanities, Arts) who wish to accelerate
                  research and course design, as well as Academic Administrators
                  and Officers looking to enhance institutional efficiency and
                  manage data operations.
                  <br />
                  <br />
                </p>
                <p className="text-sm text-blue-800 text-justify">
                  <strong>Note:</strong> <br />
                  i. For effective hands-on training, participants are
                  encouraged to bring their own laptops.
                  <br />
                  ii. Participants are required to make their own arrangements
                  for stay.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form (Untouched) */}
      <section id="register" className="py-20 bg-white/50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-blue-900 mb-4">
              Register Now
            </h2>
            <p className="text-xl text-gray-600">
              Secure your spot in this transformative program
            </p>
          </div>

          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-100">
            {seatsRemaining > 0 ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* --- Form Fields --- */}
                <div>
                  <label className="block text-sm font-semibold text-blue-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="uppercase w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="lowercase w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      Designation *
                    </label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      Department *
                    </label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      Date of Birth *
                    </label>
                    <input
                      type="text"
                      name="dob"
                      value={formData.dob}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                      placeholder="DD/MM/YYYY"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-blue-700 mb-2">
                      WhatsApp Number*
                    </label>
                    <input
                      type="text"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-blue-700 mb-2">
                    Affiliation *
                  </label>
                  <input
                    type="text"
                    name="affiliation"
                    value={formData.affiliation}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                    placeholder="University/Institution"
                  />
                </div>
                {/* --- 1. NEW: PHOTO UPLOAD SECTION --- */}
                <div
                  className={`p-4 rounded-xl border transition-all ${
                    photoFileId
                      ? "bg-purple-50 border-purple-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <Camera className="w-4 h-4 mr-2" />
                      {photoFileId
                        ? "Photo Uploaded"
                        : "Upload Photograph (Passport Size) *"}
                    </span>
                    {photoFileId && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </label>

                  {/* IMAGE PREVIEW */}
                  {photoFile && (
                    <div className="mb-3 flex flex-col items-center">
                      <img
                        src={URL.createObjectURL(photoFile)}
                        alt="Uploaded preview"
                        className="w-32 h-40 object-cover rounded-lg border"
                      />
                    </div>
                  )}

                  {/* FILE INPUT */}
                  {!photoFileId && (
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      onChange={handlePhotoChange}
                      className="block w-full text-sm text-slate-500
        file:mr-4 file:py-2 file:px-4
        file:rounded-full file:border-0
        file:text-sm file:font-semibold
        file:bg-purple-100 file:text-purple-700
        hover:file:bg-purple-200 mb-2"
                    />
                  )}

                  {/* UPLOAD BUTTON */}
                  {photoFile && !photoFileId && (
                    <button
                      type="button"
                      onClick={handleUploadPhoto}
                      disabled={isPhotoUploading}
                      className="bg-purple-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-purple-700"
                    >
                      {isPhotoUploading ? "Uploading..." : "Upload Photo"}
                    </button>
                  )}

                  {/* CHANGE PHOTO */}
                  {photoFileId && (
                    <button
                      type="button"
                      onClick={() => {
                        setPhotoFile(null);
                        setPhotoFileId(null);
                      }}
                      className="mt-2 text-sm font-semibold text-red-600 hover:underline"
                    >
                      Change photo
                    </button>
                  )}
                </div>

                {/* --- NOC UPLOAD SECTION --- */}
                <div
                  className={`p-4 rounded-xl border transition-all ${
                    uploadedFileId
                      ? "bg-green-50 border-green-200"
                      : "bg-blue-50 border-blue-100"
                  }`}
                >
                  <label className="block text-sm font-semibold text-blue-900 mb-2 flex items-center justify-between">
                    <span className="flex items-center">
                      <Upload className="w-4 h-4 mr-2" />
                      {uploadedFileId
                        ? "NOC Uploaded Successfully"
                        : "Upload NOC (Optional)"}
                    </span>
                    {uploadedFileId && (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </label>

                  {!uploadedFileId && (
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="block w-full text-sm text-slate-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-full file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-100 file:text-blue-700
                        hover:file:bg-blue-200 mb-2"
                    />
                  )}

                  <div className="flex flex-wrap items-center gap-3 mt-3">
                    {nocFile && !uploadedFileId && (
                      <button
                        type="button"
                        onClick={handleUploadNOC}
                        disabled={isUploading}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 flex items-center"
                      >
                        {isUploading ? "Uploading..." : "Click to Upload"}
                      </button>
                    )}

                    {uploadedFileId && (
                      <button
                        type="button"
                        onClick={handlePreviewNOC}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 flex items-center gap-2"
                      >
                        <FileText className="w-4 h-4" /> Preview File
                      </button>
                    )}

                    {uploadedFileId && (
                      <button
                        type="button"
                        onClick={handleResetFile}
                        className="bg-amber-100 text-amber-800 border border-amber-200 px-4 py-2 rounded-lg text-sm font-semibold hover:bg-amber-200 flex items-center gap-2"
                      >
                        Update / Change
                      </button>
                    )}

                    {uploadedFileId && isPreviewed && (
                      <span className="text-xs text-green-700 font-semibold bg-green-100 px-2 py-1 rounded">
                        ✓ Verified
                      </span>
                    )}
                  </div>
                  {!uploadedFileId && (
                    <p className="text-xs text-blue-600 mt-2">
                      Format: PDF, JPG, PNG (Max 5MB)
                    </p>
                  )}
                </div>

                {/* --- PREVIEW MODAL (PIP STYLE) --- */}
                {showPreviewModal && (
                  <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-4xl h-[80vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-fadeIn">
                      <div className="flex justify-between items-center p-4 border-b bg-gray-50">
                        <h3 className="font-bold text-gray-700 flex items-center">
                          <FileText className="w-5 h-5 mr-2 text-blue-600" />
                          Document Preview
                        </h3>
                        <button
                          onClick={() => setShowPreviewModal(false)}
                          className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                        >
                          <X className="w-6 h-6 text-gray-600" />
                        </button>
                      </div>
                      <div className="flex-1 bg-gray-100 p-2 relative">
                        <iframe
                          src={previewUrl}
                          title="NOC Preview"
                          className="w-full h-full rounded-lg border border-gray-300"
                          style={{ backgroundColor: "white" }}
                        />
                      </div>
                      <div className="p-4 border-t bg-gray-50 text-right">
                        <button
                          onClick={() => setShowPreviewModal(false)}
                          className="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700"
                        >
                          Close Preview
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preferred Contact *
                  </label>
                  <select
                    name="contactMethod"
                    value={formData.contactMethod}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 transition-all outline-none"
                  >
                    <option value="">Select...</option>
                    <option value="Email">Email</option>
                    <option value="Phone">Phone</option>
                    <option value="WhatsApp">WhatsApp</option>
                  </select>
                </div>

                <button
                  disabled={loading} //|| !isPreviewed}
                  className={`w-full py-4 rounded-xl font-bold text-lg transform hover:-translate-y-1 transition-all
                    ${
                      !isPreviewed
                        ? "bg-gradient-to-r from-red-600 to-red-600 text-white hover:shadow-2xl"
                        : "bg-gradient-to-r from-red-600 to-red-600 text-white hover:shadow-2xl"
                    }`}
                >
                  {loading
                    ? "Submitting..."
                    : !isPreviewed
                    ? "Submit"
                    : "Submit"}
                </button>
              </form>
            ) : (
              <div className="text-center py-10">
                <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">
                  Registration Closed
                </h3>
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function OrganizingCommittee() {
  return (
    <section id="team" className="py-14 bg-slate-50 font-sans">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-light bg-blue-600 text-white text-white-900  font-serif inline-block px-6 py-4 rounded-full shadow-lg">
            Programme Leadership
          </h2>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-9">
          {/* Card */}
          {[
            {
              img: "/vc.jpg",
              alt: "Prof. Umesh Rai",
              name: "Prof. Umesh Rai",
              role: "Patron",
              desc: "Hon’ble Vice Chancellor",
              uni: "University of Jammu",
            },
            {
              img: "/sk.png",
              alt: "Prof. S. K. Pandita",
              name: "Prof. S. K. Pandita",
              role: "Advisor",
              desc: "Director, MMTTC",
              uni: "University of Jammu",
            },
            {
              img: "/alka.png",
              alt: "Prof. Alka Sharma",
              name: "Prof. Alka Sharma",
              role: "Convenor",
              desc: "Director, SIIEDC",
              uni: "University of Jammu",
            },
            {
              img: "/jat.png",
              alt: "Dr. Jatinder Manhas",
              name: "Dr. Jatinder Manhas",
              role: "Coordinator",
              desc: "Associate Director, SIIEDC",
              uni: "University of Jammu",
            },
          ].map((m, i) => (
            <div
              key={i}
              className="bg-white border border-gray-200 rounded-lg p-6  text-center"
            >
              <img
                src={m.img}
                alt={m.name}
                className="w-20 h-20 mx-auto rounded-full object-cover mb-6"
              />

              <h3 className="text-sm  text-gray-900 uppercase font-serif bg-blue-100 inline-block px-3 py-3 ">
                {m.name}
              </h3>

              <p className="text-sm font-medium text-blue-700 mt-0.5 font-serif ">
                {m.role}
              </p>

              <p className="text-xs text-gray-500 mt-1 leading-snug font-serif">
                {m.desc}
              </p>
              <p className="text-xs text-gray-400 mt-1 font-serif">{m.uni}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ==========================================
// COMPONENT: Admin Login
// ==========================================

function AdminLogin({ onLoginSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        // If this succeeds, user is already logged in
        await account.get();
        onLoginSuccess();
      } catch (err) {
        // ERROR HANDLING:
        // Appwrite throws "User (role: guests) missing scopes" if not logged in.
        // We swallow this error because it's expected for a login page.
        console.log("No active session found (Guest mode)");
      }
    };
    checkSession();
  }, [onLoginSuccess]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await account.createEmailPasswordSession(email, password);
      onLoginSuccess();
    } catch (err) {
      setLoading(false);
      // Handle Rate Limit or other errors
      if (err.message && err.message.includes("Rate limit")) {
        setError(
          "⚠️ Too many attempts. You have been temporarily blocked. Please wait 15-30 minutes."
        );
      } else if (
        err.message &&
        err.message.includes("prohibited when a session is active")
      ) {
        onLoginSuccess(); // Already logged in
      } else {
        setError(err.message);
      }
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="bg-white p-10 rounded-3xl shadow-2xl max-w-md w-full border border-gray-100">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <LogIn className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Faculty Login</h2>
        </div>

        {error && (
          <div
            className={`p-4 rounded-lg mb-4 text-sm text-center ${
              error.includes("Too many")
                ? "bg-amber-50 text-amber-800 border border-amber-200"
                : "bg-red-50 text-red-600"
            }`}
          >
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="space-y-4">
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 outline-none"
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-blue-500 outline-none"
            required
          />
          <button
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Verifying..." : "Login"}
          </button>
        </form>
      </div>
    </div>
  );
}
